import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAdminAccount } from "@/hooks/use-admin-accounts";

export const Route = createFileRoute("/_admin/accounts/$id")({
  component: AdminAccountDetailPage,
});

function AdminAccountDetailPage() {
  const { t } = useTranslation(["accounts", "common"]);
  const { id } = Route.useParams();
  const { data: account, isLoading, isError } = useAdminAccount(id);

  if (isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  }

  if (isError || !account) {
    return <div className="p-6 text-sm text-red-700">{t("errors.loadDetailFailed")}</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <header className="space-y-1">
        <Link
          to="/accounts"
          search={{ org_id: undefined }}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          ← {t("list.title")}
        </Link>
        <h1 className="text-2xl font-semibold text-slate-900">{account.name}</h1>
        <p className="text-sm text-slate-500">{t(`accountType.${account.account_type}`)}</p>
        {account.deleted_at && (
          <span className="inline-block rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-700">
            {t("detail.deletedBadge")}
          </span>
        )}
      </header>

      <section className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-700">{t("sections.contact")}</h2>
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-slate-500">{t("fields.contactEmail")}</dt>
              <dd>{account.contact_email ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-slate-500">{t("fields.contactPhone")}</dt>
              <dd>{account.contact_phone ?? "—"}</dd>
            </div>
          </dl>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-700">
            {t("sections.mailingAddress")}
          </h2>
          <p className="text-sm">
            {account.mailing_address_line1 ?? "—"}
            {account.mailing_address_line2 ? `, ${account.mailing_address_line2}` : ""}
            <br />
            {[account.mailing_city, account.mailing_state, account.mailing_postal_code]
              .filter(Boolean)
              .join(", ") || "—"}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-700">
            {t("sections.billingAddress")}
          </h2>
          <p className="text-sm">
            {account.billing_address_line1 ?? "—"}
            {account.billing_address_line2 ? `, ${account.billing_address_line2}` : ""}
            <br />
            {[account.billing_city, account.billing_state, account.billing_postal_code]
              .filter(Boolean)
              .join(", ") || "—"}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-700">{t("sections.additional")}</h2>
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-slate-500">{t("fields.taxId")}</dt>
              <dd>{account.tax_id ?? "—"}</dd>
            </div>
          </dl>
        </div>
      </section>

      <p className="text-xs text-slate-400">
        org_id: <code>{account.org_id}</code>
      </p>
    </div>
  );
}
